import type { Drama } from '@/types/content';

interface DramaInfoProps {
  drama: Drama;
  isFavorite: boolean;
  onToggleFavorite: () => void;
}

export default function DramaInfo({ drama, isFavorite, onToggleFavorite }: DramaInfoProps) {
  const isOngoing = drama.status === '连载';
  const meta = [
    { label: '剧集状态', value: drama.status, icon: 'ri-signal-tower-line' },
    { label: '总集数', value: `全 ${drama.episodes} 集`, icon: 'ri-play-list-2-line' },
    { label: '首播时间', value: drama.firstAirDate, icon: 'ri-calendar-line' },
    { label: '最近更新', value: drama.updateDate, icon: 'ri-refresh-line' },
    { label: '导演', value: drama.director, icon: 'ri-user-star-line' },
    { label: '主演', value: drama.cast.join(' / '), icon: 'ri-group-line' },
  ];

  return (
    <section className="grid grid-cols-1 gap-6 md:grid-cols-[220px_1fr]">
      <div className="mx-auto w-44 overflow-hidden rounded-lg md:mx-0 md:w-full" style={{ aspectRatio: '3 / 4' }}>
        <img
          src={drama.cover}
          alt={`${drama.title} 剧照海报`}
          title={`${drama.title} - 剧情介绍与角色资料`}
          className="h-full w-full object-cover object-top"
        />
      </div>

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            {drama.title}
          </h1>
          <span
            className={`rounded-full px-3 py-1 text-xs font-label ${
              isOngoing ? 'bg-accent-500 text-primary-950' : 'bg-primary-500 text-background-50'
            }`}
          >
            {drama.status}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {drama.genres.map((genre) => (
            <span key={genre} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
              {genre}
            </span>
          ))}
        </div>

        <dl className="mt-5 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          {meta.map((item) => (
            <div key={item.label} className="flex items-start gap-2 text-sm">
              <i className={`${item.icon} mt-0.5 text-primary-400`} />
              <dt className="shrink-0 text-foreground-400">{item.label}：</dt>
              <dd className="min-w-0 text-foreground-800">{item.value}</dd>
            </div>
          ))}
        </dl>

        <button
          type="button"
          onClick={onToggleFavorite}
          aria-pressed={isFavorite}
          className={`mt-6 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 font-label text-sm transition-colors ${
            isFavorite
              ? 'bg-accent-500 text-primary-950 hover:bg-accent-400'
              : 'border border-background-300 text-foreground-800 hover:border-accent-400 hover:text-accent-600'
          }`}
        >
          <i className={isFavorite ? 'ri-heart-fill text-lg' : 'ri-heart-line text-lg'} />
          {isFavorite ? '已收藏（点击取消）' : '收藏这部剧'}
        </button>
      </div>
    </section>
  );
}